<template>
    <div class="pb-5 pt-5">
        <div class="row m-0">
            <div class="col-12">
                <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-1">
                    <h5 class="title mt-2">{{ $tl("labels.routes.booking.config.integration") }}</h5>
                    <a target="_blank" :href="widgetLink">
                        <button
                            :disabled="
                                apiKey === null ||
                                !restaurant ||
                                (!restaurant.stripe_client_id && (!restaurant.payplug_public_key || !restaurant.payplug_secret_key))
                            "
                            class="btn btn-sm btn-success btn-sm btn-circle me-1"
                            type="button">
                            {{ $tl("labels.widget.show") }}
                        </button>
                    </a>
                </div>
            </div>
        </div>
        <overlay-layout
            :display-overlay="!restaurant || (!restaurant.stripe_client_id && (!restaurant.payplug_public_key || !restaurant.payplug_secret_key))"
            class="m-1">
            <template v-slot:content>
                <div class="row m-0 mb-3">
                    <div class="col-12">
                        <div class="border-light b-radius-20 p-4">
                            <div class="row mb-3">
                                <div class="col-12">
                                    <strong>{{ $tl("infos.widget.infosIntegrationWebsite.title") }}</strong>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <p>
                                        {{
                                            $t("infos.widget.infosIntegrationWebsite.content1", {
                                                type: $tl("labels.widget.clickAndCollect"),
                                            })
                                        }}
                                    </p>
                                    <p>
                                        Nous vous recommandons d'intégrer le module directement dans votre site internet en suivant les étapes
                                        ci-dessous. Pour un affichage optimal, il est recommandé d'utiliser la largeur totale de l'écran en limitant
                                        les marges gauche et droite.
                                    </p>
                                    <p>
                                        {{ $tl("infos.widget.infosIntegrationWebsite.content2") }}
                                    </p>
                                    <p class="mb-0 text-danger">
                                        {{ $tl("infos.widget.infosIntegrationWebsite.content3") }}
                                    </p>
                                    <br />
                                    <button
                                        :disabled="!has_right_to_update_config"
                                        @click="showSendWidgetDocToDevModal = true"
                                        class="btn btn-sm btn-success btn-sm btn-circle"
                                        type="button">
                                        {{ $tl("labels.widget.sendInstructions") }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row m-0 mb-3">
                    <div class="col-12">
                        <div class="border-light b-radius-20 p-4">
                            <div class="row">
                                <div class="col-12">
                                    <strong>{{ $tl("labels.widget.integrationWebsite") }}</strong>
                                </div>
                            </div>
                            <div class="row mt-3">
                                <div class="col-md-5">
                                    <label class="form-label">{{ $tl("labels.widget.shortLink") }}</label>
                                </div>
                                <div class="col-md-7">
                                    <div class="d-flex align-items-center">
                                        <span class="me-2">{{ appUrl }}/vente-en-ligne/</span>
                                        <input
                                            type="text"
                                            class="form-control"
                                            style="width: 45%"
                                            maxlength="40"
                                            v-model="shortlink"
                                            :disabled="!has_right_to_update_config" />
                                        <button
                                            :disabled="!has_right_to_update_config || isSavingShortlink"
                                            :class="{ disabled: !has_right_to_update_config || isSavingShortlink }"
                                            @click="saveShortLink"
                                            class="btn btn-sm btn-success btn-sm btn-circle ms-2"
                                            type="button">
                                            {{ $tl("labels.form.actions.save") }}
                                        </button>
                                    </div>
                                    <div>
                                        <small class="text-muted">{{ $t("infos.widget.shortLink", { max: 40 }) }}</small>
                                    </div>
                                </div>
                            </div>
                            <div class="row mt-3">
                                <div class="alert alert-danger col-md-12 pt-1" v-if="apiKeyError">
                                    {{ apiKeyError }}
                                </div>
                                <div class="col-md-5">
                                    <label class="form-label">{{ $tl("labels.widget.apiKey") }}</label>
                                </div>
                                <div class="col-md-7">
                                    <div class="mb-3 code-source" v-if="apiKey">
                                        {{ apiKey }}
                                    </div>
                                    <button
                                        :disabled="!has_right_to_update_config"
                                        @click="askApiKey"
                                        class="btn btn-sm btn-success btn-sm btn-circle"
                                        type="button">
                                        <span v-if="!apiKey">{{ $tl("labels.widget.genApiKey") }}</span
                                        ><span v-else>{{ $tl("labels.widget.genNewApiKey") }}</span></button
                                    ><br />
                                    <small v-if="apiKey" class="text-danger">{{ $tl("infos.widget.oldKeyRevoked") }}</small>
                                </div>
                            </div>

                            <div class="row mt-3">
                                <div class="col-md-5 pt-1" v-if="apiKey !== null">
                                    <label class="form-label">{{ $tl("labels.widget.integrationMethod") }}</label>
                                </div>
                                <div class="col-md-7 pt-1" v-if="apiKey !== null">
                                    <p>
                                        <strong>{{ $t("labels.stepX", { step: 1 }) }}{{ $tl("labels.colon") }}</strong>
                                        {{ $t("infos.widget.stepsIntegration.1") }}
                                    </p>

                                    <div class="code-source mb-3">&lt;div id="click-and-collect"&gt;&lt;/div&gt;</div>

                                    <p>
                                        <strong>{{ $t("labels.stepX", { step: 2 }) }}{{ $tl("labels.colon") }}</strong>
                                        {{ $t("infos.widget.stepsIntegration.2") }}
                                    </p>

                                    <div class="code-source mb-3">
                                        <span class="d-block" v-for="(js, index) in jsToInclude" :key="index">{{ js }}</span>
                                    </div>

                                    <p>
                                        <strong>{{ $t("labels.stepX", { step: 3 }) }}{{ $tl("labels.colon") }}</strong>
                                        {{ $t("infos.widget.stepsIntegration.3") }}
                                    </p>

                                    <div class="code-source mb-3">{{ classFunctionName }}('click-and-collect', '{{ apiKey }}');</div>

                                    <p class="mb-0">
                                        <strong
                                            >{{ $t("labels.stepX", { step: 4 }) }} ({{ $tl("labels.optionalF") }}){{ $tl("labels.colon") }}</strong
                                        >
                                        {{ $t("infos.widget.stepsIntegration.4") }}<br />
                                        {{ $tl("infos.widget.optionalParams") }}
                                    </p>
                                    <ol>
                                        <li class="list-style-inherit">
                                            {{ $tl("infos.widget.clickAndCollect.widgetBlockTypeDescription") }}
                                            <ul>
                                                <li class="list-style-inherit">{{ $tl("infos.widget.clickAndCollect.options.collect") }}</li>
                                                <li class="list-style-inherit">{{ $tl("infos.widget.clickAndCollect.options.delivery") }}</li>
                                                <li class="list-style-inherit">{{ $tl("infos.widget.clickAndCollect.options.dispatch") }}</li>
                                            </ul>
                                        </li>
                                    </ol>
                                    <div class="code-source">{{ classFunctionName }}('click-and-collect', '{{ apiKey }}', { type: 'delivery' });</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <FacebookIntegration class="m-0 mb-3" :module="MODULE_TYPE_CLICK_AND_COLLECT.value" />

                <InstagramIntegration class="m-0 mb-3" :module="MODULE_TYPE_CLICK_AND_COLLECT.value" />

                <GoogleLinkIntegration class="m-0 mb-3" />
            </template>
            <template v-slot:overlay>
                <div class="is-white d-flex justify-content-center align-items-center">
                    <div class="text-center">
                        <p>{{ $tl("infos.widget.clickAndCollect.connectStripeAccountMessage") }}</p>
                        <router-link v-if="isYservices" :to="{ name: 'restaurants' }" class="btn btn-circle btn-success">
                            {{ $tl("infos.widget.clickAndCollect.addStripeAccount") }}
                        </router-link>
                        <router-link v-else :to="{ name: 'restaurants.edit', params: { restaurant_id } }" class="btn btn-circle btn-success">
                            {{ $tl("infos.widget.clickAndCollect.addStripeAccount") }}
                        </router-link>
                    </div>
                </div>
            </template>
        </overlay-layout>
        <send-click-and-collect-widget-doc-to-dev-modal
            ref="sendClickAndCollectWidgetDocToDevModal"
            v-if="showSendWidgetDocToDevModal"
            @close="showSendWidgetDocToDevModal = false"
            :restaurant_id="restaurant_id" />
    </div>
</template>

<script>
import sendClickAndCollectWidgetDocToDevModal from "../../../components/Modals/clickAndCollect/sendClickAndCollectWidgetDocToDevModal";
import OverlayLayout from "../../../components/layouts/OverlayLayout";
import ModuleTypesEnum from "../../../mixins/enums/ModuleTypesEnum.js";
import FacebookIntegration from "../../../components/widgets/FacebookIntegration.vue";
import InstagramIntegration from "../../../components/widgets/InstagramIntegration.vue";
import GoogleLinkIntegration from "../../../components/widgets/GoogleLinkIntegration.vue";

export default {
    data() {
        return {
            appUrl: APP_URL,
            loading: false,
            errors: null,
            hasKey: false,
            apiKey: null,
            apiKeyError: null,
            showSendWidgetDocToDevModal: false,
            has_dispatch_option: false,
            shortlink: "",
            isSavingShortlink: false,
        };
    },
    mixins: [ModuleTypesEnum],
    computed: {
        widgetLink() {
            if (
                !this.apiKey ||
                !this.restaurant ||
                (!this.restaurant.stripe_client_id && (!this.restaurant.payplug_public_key || !this.restaurant.payplug_secret_key))
            )
                return "#";
            if (this.shortlink) return `/vente-en-ligne/${this.shortlink}`;
            return `/click_and_collect/widget/public/${this.apiKey}/page`;
        },
        jsToInclude() {
            const jsToInclude = [`<isplease src="${APP_URL}/click_and_collect/js/widget.js"></isplease>`.replace(/isplease/g, "script")];

            if (this.has_dispatch_option) {
                jsToInclude.push(`<isplease src="https://embed.sendcloud.sc/spp/1.0.0/api.min.js"></isplease>`.replace(/isplease/g, "script"));
            }

            return jsToInclude;
        },
        restaurant_id() {
            return this.$route.params.restaurant_id;
        },
        restaurant() {
            return this.$store.getters["restaurants/findRestaurantById"](this.restaurant_id);
        },
        rights: function () {
            return this.$store.getters["users/formattedRights"];
        },
        has_right_to_update_config() {
            return this.rights.includes("click_and_collect.config.update");
        },
        classFunctionName() {
            return this.isNoShow ? "window.initNoShowClickAndCollect" : "window.initYServicesClickAndCollect";
        },
    },
    created() {
        this.fetchHasApiKey();
    },
    methods: {
        fetchHasApiKey() {
            this.apiKeyError = null;

            this.$store
                .dispatch("widgets/fetchClickAndCollectApiKey", {
                    restaurant_id: this.restaurant_id,
                })
                .then((response) => {
                    this.apiKey = response.data.key;
                    this.shortlink = response.data.shortlink;
                    this.has_dispatch_option = response.data.has_dispatch_option;
                })
                .catch((error) => {
                    this.apiKeyError = this.getErrorMsgFromErrorResponse(error);
                });
        },
        askApiKey() {
            this.apiKeyError = null;

            this.$store
                .dispatch("widgets/generateClickAndCollectApiKey", {
                    restaurant_id: this.restaurant_id,
                })
                .then((response) => {
                    this.apiKey = response.data.key;
                    this.hasKey = true;
                })
                .catch((error) => {
                    this.apiKeyError = this.getErrorMsgFromErrorResponse(error);
                });
        },
        saveShortLink() {
            this.isSavingShortlink = true;

            axios
                .post(`/api/click_and_collect/${this.restaurant_id}/widget/shortlink`, {
                    shortlink: this.shortlink,
                })
                .then((response) => {
                    this.notifySuccess(response);
                })
                .catch((error) => {
                    this.notifyError(error);
                })
                .finally(() => (this.isSavingShortlink = false));
        },
    },
    watch: {
        shortlink(newVal, oldVal) {
            if (!newVal || newVal.length < oldVal.length) {
                return;
            }

            if (newVal.length > 0 && !newVal.replace(oldVal, "").match(/^[a-z_\-]+$/)) {
                this.shortlink = oldVal;
            }
        },
    },
    components: {
        sendClickAndCollectWidgetDocToDevModal,
        OverlayLayout,
        FacebookIntegration,
        InstagramIntegration,
        GoogleLinkIntegration,
    },
};
</script>

<style scoped>
.list-style-inherit {
    list-style: inherit;
}

button:disabled {
    cursor: default;
}
</style>
